import type { ToolCall } from '@elara/client-core/models/ToolCallModel';
import type { IconName } from '@elara/client-core/ui-contracts/icon-name';

import { useState } from 'react';

import { cn } from '@elara/client-core/lib/utils';

import { IconSizes, MaterialIcon } from '#ui/MaterialIcon.js';

import { ToolBadge } from './ToolBadge.js';

export interface ToolBadgeGroupProps {
  icon: IconName;
  toolCalls: ToolCall[];
  summary: string;
}

/** One traveler-facing activity disclosure. A single call keeps its existing badge presentation. */
export function ToolBadgeGroup({ icon, toolCalls, summary }: ToolBadgeGroupProps) {
  const [isOpen, setIsOpen] = useState(false);
  if (toolCalls.length === 0) return null;
  if (toolCalls.length === 1) return <ToolBadge toolCall={toolCalls[0]!} variant="square" className="w-full" />;

  return (
    <div className="w-full">
      <button
        type="button"
        aria-label={summary}
        aria-expanded={isOpen}
        onClick={() => setIsOpen((value) => !value)}
        className="flex items-center gap-2 text-sm text-muted-foreground/70 w-full cursor-pointer hover:text-foreground transition-colors"
      >
        <MaterialIcon icon={icon} size={IconSizes.xs} className="shrink-0" />
        <span className="truncate">{summary}</span>
        <MaterialIcon
          icon="keyboard_arrow_down"
          size={IconSizes.xs}
          className={cn('shrink-0 transition-transform -ml-1', isOpen && 'rotate-180')}
        />
      </button>
      {isOpen && (
        <div className="mt-3 ml-1 pl-3 border-l border-border/60 space-y-3">
          {toolCalls.map((toolCall) => (
            <ToolBadge key={toolCall.id} toolCall={toolCall} variant="square" className="w-full" />
          ))}
        </div>
      )}
    </div>
  );
}
